<template>
  <div class="relative">
    <!-- Tabs -->
    <div class="p-4">
      <div class="bg-slate-100/80 rounded-2xl p-1.5 flex gap-2 shadow-inner">
        <button
          v-for="tab in filterTabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="flex-1 py-2.5 px-3 rounded-xl text-sm font-semibold transition-all duration-300 relative text-center"
          :class="[
            activeTab === tab.id
              ? 'bg-white text-primary shadow-lg shadow-primary/20'
              : 'text-slate-500 hover:text-slate-700 hover:bg-white/50',
          ]"
        >
          {{ tab.name }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="px-4">
      <!-- Error state -->
      <div v-if="error && !displayLoading" class="flex flex-col items-center justify-center py-16">
        <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg class="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div class="text-lg font-semibold text-gray-700 mb-2">Erreur de chargement</div>
        <div class="text-sm text-gray-500 mb-4 text-center max-w-xs">{{ error }}</div>
        <button @click="loadPackages" class="px-6 py-2 bg-primary text-white rounded-full font-semibold text-sm hover:bg-primary-dark transition-colors">
          Réessayer
        </button>
      </div>

      <!-- Empty state -->
      <div v-else-if="!displayLoading && displayPackages.length === 0" class="flex flex-col items-center justify-center py-16">
        <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <ImgComponent src="/images/packageDelivery/package.png" class="w-10 h-10 opacity-50" />
        </div>
        <div class="text-lg font-semibold text-gray-700 mb-2">Aucun colis {{ activeTab === 'in_transit' ? 'en transit' : activeTab === 'delivered' ? 'délivré' : activeTab === 'drafts' ? 'en brouillon' : 'annulé' }}</div>
        <div class="text-sm text-gray-500 text-center max-w-xs mb-4">
          {{ activeTab === 'in_transit' ? 'Vous n\'avez aucune livraison en cours pour le moment.' : activeTab === 'drafts' ? 'Vous n\'avez aucun brouillon en cours.' : '' }}
        </div>
      </div>

      <!-- Packages list or loading shimmer -->
      <div v-else class="flex flex-col gap-4" :class="{ shimmerContainer: displayLoading }">
        <template
          v-for="(el, index) in displayPackages"
          :key="el.id || index"
        >
          <div
            class="cursor-pointer"
            @click="showPackageDetails(el)"
          >
            <div class="liquidGlassCard p-4 rounded-3xl">
              <div class="grid grid-cols-3 gap-4">
                <div class="col-span-2">
                  <div class="flex items-center gap-2 mb-2" @click.stop>
                    <div
                      class="rounded-full px-4 py-1 bg-primary text-white font-bold text-xs"
                    >
                      <!-- UTILISATION DU NOUVEAU STATUT (avec filet de sécurité) -->
                      {{ activeTab === 'drafts' ? 'Brouillon' : ( getStatusDisplay(el.status)) }}
                    </div>
                    <button
                      v-if="activeTab !== 'drafts'"
                      @click.stop.prevent="resendPackage(el)"
                      class="rounded-full px-3 py-1 bg-blue-500 text-white font-bold text-xs hover:bg-blue-600 transition-colors"
                    >
                      Renvoyer
                    </button>
                    <button
                      v-if="activeTab === 'drafts'"
                      @click.stop.prevent="resumeDraft(el)"
                      class="rounded-full px-3 py-1 bg-green-500 text-white font-bold text-xs hover:bg-green-600 transition-colors"
                    >
                      Reprendre
                    </button>
                    <button
                      v-if="activeTab === 'drafts'"
                      @click.stop.prevent="deleteDraft(el.id)"
                      class="rounded-full px-3 py-1 bg-red-500 text-white font-bold text-xs hover:bg-red-600 transition-colors"
                    >
                      Supprimer
                    </button>
                    <!-- {{ el.currentTimelineStatus }} -->
                    <button
                      v-if="el.riderId"
                      @click.stop.prevent="openRiderChat(el)"
                      class="rounded-full px-3 py-1 bg-primary text-white font-bold text-xs hover:bg-primary-600 transition-colors flex items-center gap-1"
                    >
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      Chat
                    </button>
                  </div>
                  <div class="title-color font-bold text-lg">#{{ el.trackingCode || el.deliveryCode || el.id }}</div>
                  
                  <!-- NOUVEAU : Affichage de la dernière étape en petit (seulement si elle existe) -->
                  <div v-if="activeTab !== 'drafts' && el.latestTimelineDescription" class="mt-1 text-xs text-primary font-medium">
                    Dernière étape : {{ el.latestTimelineDescription }}
                  </div>

                  <div class="mt-3 text-sm text-gray-600">
                    {{ formatDate(el.createdAt) }}
                  </div>
                  <div class="mt-1 title-color text-sm text-gray-600">
                    {{ el.srcDstLocation?.srcPosition?.label || el.pickup?.address || el.pickupAddress || el.pickupLabel || 'Position de départ' }} → {{ el.srcDstLocation?.dstPosition?.label || el.destination?.address || el.destinationAddress || el.destinationLabel || 'Position d\'arrivée' }}
                  </div>
                </div>
                <div>
                  <ImgComponent
                    class="w-full"
                    :src="
                      el.packageType === 'document'
                        ? '/images/packageDelivery/document.png'
                        : '/images/packageDelivery/package.png'
                    "
                  />
                </div>
              </div>
              <div class="py-5 px-5" v-if="activeTab !== 'drafts'">
                <div
                  class="relative rounded-full bg-gray-200 h-2 flex items-center justify-center"
                >
                  <!-- UTILISATION DU NOUVEAU POURCENTAGE (avec filet de sécurité) -->
                  <div
                    class="absolute left-0 h-full rounded-full bg-gradient-to-r from-white to-primary transition-all duration-500"
                    :style="{
                      width: el.progressPercentage !== undefined ? el.progressPercentage + '%' : getProgressWidth(el.status, el.stepsTimeline)
                    }"
                  ></div>
                  <div
                    class="absolute left-0 h-full flex items-center justify-center transition-all duration-500"
                    :style="{
                      left: el.progressPercentage !== undefined ? el.progressPercentage + '%' : getProgressWidth(el.status, el.stepsTimeline)
                    }"
                  >
                    <div
                      class="absolute w-12 h-12 rounded-full bg-primary opacity-20"
                    ></div>
                    <div
                      class="w-10 h-10 bg-primary rounded-full p-2 absolute shadow-lg"
                    >
                      <ColorImg
                        src="/images/icons/delivery-bike.png"
                        alt=""
                        class="bg-black/60"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Shimmer placeholders when loading -->
        <template v-if="displayLoading" v-for="i in 10" :key="`shimmer-${i}`">
          <div class="liquidGlassCard p-4 rounded-3xl">
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-2">
                <div class="h-6 bg-gray-200 rounded-full mb-2 w-20"></div>
                <div class="h-5 bg-gray-200 rounded w-32 mb-3"></div>
                <div class="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                <div class="h-4 bg-gray-200 rounded w-48"></div>
              </div>
              <div class="h-20 bg-gray-200 rounded-lg"></div>
            </div>
            <div class="py-5 px-5">
              <div class="h-2 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Package Details Dialog -->
    <PackageDetailsDialog ref="detailsDialog" />

    <!-- Draft Details Dialog -->
    <DraftDetailsDialog ref="draftDetailsDialog" @delete="deleteDraft" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { bus } from '@core/events/eventSystem'
import constants from '@core/utils/constants'
import { getStore } from '@core/store'
import { packageToWizardDraft } from '../resendUtils'
import PackageDetailsDialog from './PackageDetailsDialog.vue'
import DraftDetailsDialog from './DraftDetailsDialog.vue'

const detailsDialog = ref(null)
const draftDetailsDialog = ref(null)

defineEmits(['startNewDeliveryRequest'])

const activeTab = ref('in_transit')
const filterTabs = [
  { name: 'Actives', id: 'in_transit' },
  { name: 'Délivrées', id: 'delivered' },
  { name: 'Annulées', id: 'canceled' },
  { name: 'Brouillons', id: 'drafts' },
]

const store = getStore('packages')
const localDrafts = ref([])

const loadLocalDrafts = () => {
  const draftsJson = localStorage.getItem('package_drafts')
  if (draftsJson) {
    try {
      const draftsMap = JSON.parse(draftsJson)
      localDrafts.value = Object.values(draftsMap).sort((a, b) => b.updatedAt - a.updatedAt)
    } catch (e) {
      console.error('Failed to parse drafts', e)
      localDrafts.value = []
    }
  } else {
    localDrafts.value = []
  }
}

// Load packages via event
const loadPackages = (status = '') => {
  bus.emit(constants.EVENTS.PACKAGES.LOAD, '')
}

// Get packages from store
const packages = computed(() => store.state.packages || [])

// Use loading and error directly from store
const isInitialLoad = ref(true)
const loading = computed(() => store.state.loading || false)
const displayLoading = computed(() => loading.value && isInitialLoad.value)

watch(loading, (newLoading) => {
  if (!newLoading) {
    isInitialLoad.value = false
  }
})
const error = computed(() => store.state.error || null)

// Filter packages by active tab (UTILISE LE NOUVEAU STATUT)
const displayPackages = computed(() => {
  if (activeTab.value === 'drafts') {
    return localDrafts.value
  }

  if (!packages.value || packages.value.length === 0) return []

  return packages.value.filter(pkg => {
    // Utilisation de la nouvelle logique du serializer (ou fallback)
    const statusToCheck = pkg.currentTimelineStatus || pkg.status;
    
    switch (activeTab.value) {
      case 'delivered':
        return statusToCheck === 'delivered';
      case 'canceled':
        return statusToCheck === 'canceled' || statusToCheck === 'cancelled';
      case 'in_transit':
        // Si c'est ni délivré ni annulé, c'est que c'est en cours ! (englobe created, in_transit, checkpoint_reached, etc.)
        return statusToCheck !== 'delivered' && statusToCheck !== 'canceled' && statusToCheck !== 'cancelled';
      default:
        return true;
    }
  })
})

watch(activeTab, (newTab) => {
  if (newTab === 'drafts') {
    loadLocalDrafts()
  }
})

const formatDate = (timestamp) => {
  if (!timestamp) return '-'
  const d = new Date(timestamp)
  return `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`
}

// Gardé en tant que "fallback de sécurité" pour les colis en cache non sérialisés
const getStatusDisplay = (status) => {
  const statusMap = {
    created: 'Colis enrégistré',
    confirmed: 'Confirmée',
    picked_up: 'Récupéré',
    inTransit: 'En transit',
    in_transit: 'En transit',
    arrived: 'Destination atteinte',
    delivered: 'Délivrée',
    canceled: 'Annulée',
    cancelled: 'Annulée'
  }
  return statusMap[status] || 'En transit'
}

// Gardé en tant que "fallback de sécurité" pour les colis en cache non sérialisés
const getProgressWidth = (status, timeline) => {
  if (status === 'delivered' || timeline?.delivered > 0) return '100%'
  if (status === 'canceled' || status === 'cancelled') return '0%'
  if (timeline?.inTransit > 0 || timeline?.in_transit > 0) return '75%'
  if (timeline?.picked_up > 0) return '50%'
  if (timeline?.confirmed > 0) return '25%'
  return '0%'
}

let pollTimer = null;

onMounted(() => {
  loadPackages(activeTab.value)
  loadLocalDrafts()

  pollTimer = setInterval(() => {
    loadPackages(activeTab.value)
  }, 5000);
})

const resumeDraft = (draft) => {
  bus.emit(constants.EVENTS.PACKAGES.WIZARD_OPEN, { draft })
}

const resendPackage = (pkg) => {
  const draft = packageToWizardDraft(pkg)
  bus.emit(constants.EVENTS.PACKAGES.WIZARD_OPEN, { draft })
}

const deleteDraft = async (draftId) => {
  const result = await window.confirmDialog({
    title: 'Supprimer le brouillon',
    message: 'Voulez-vous vraiment supprimer ce brouillon ? Cette action est irréversible.',
    successText: 'Supprimer',
    cancelText: 'Annuler'
  });

  if (!result || !result.accepted) return;

  const draftsJson = localStorage.getItem('package_drafts')
  if (draftsJson) {
    try {
      const draftsMap = JSON.parse(draftsJson)
      delete draftsMap[draftId]
      localStorage.setItem('package_drafts', JSON.stringify(draftsMap))
      loadLocalDrafts()
    } catch (e) {
      console.error('Failed to parse drafts', e)
    }
  }
}

onUnmounted(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
})

function showPackageDetails(pkg) {
  if (activeTab.value === 'drafts') {
    if (draftDetailsDialog.value) {
      draftDetailsDialog.value.showDetails(pkg)
    }
  } else {
    if (detailsDialog.value) {
      detailsDialog.value.showDetails(pkg)
    }
  }
}

function openRiderChat(pkg) {
  bus.emit('chat.open_with_rider', pkg)
}
</script>